import React, { useState, useRef, useEffect } from 'react';
import { ModernButton } from '../ui/ModernButton';
import imageUploadService from '../../services/ImageUploadService';

export function ImageGalleryPage({ onBack }) {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [images, setImages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const fileInputRef = useRef(null);

    // Load images on component mount
    useEffect(() => {
        loadImages();
    }, []);

    const loadImages = async () => {
        setIsLoading(true);
        try {
            const imagesData = await imageUploadService.getImages();
            setImages(imagesData);
        } catch (error) {
            console.error('Error loading images:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageClick = async (imageUrl) => {
        try {
            await navigator.clipboard.writeText(imageUrl);
            // Show notification
            showNotification('Bild-URL in die Zwischenablage kopiert!', 'success');
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = imageUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            showNotification('Bild-URL in die Zwischenablage kopiert!', 'success');
        }
    };

    const showNotification = (message, type = 'success') => {
        // Create notification element
        const notification = document.createElement('div');
        notification.className = `fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 transform translate-x-full ${
            type === 'success' 
                ? 'bg-green-600 text-white border border-green-700' 
                : 'bg-red-600 text-white border border-red-700'
        }`;
        notification.style.backgroundColor = type === 'success' ? '#059669' : '#dc2626';
        notification.style.color = '#ffffff';
        notification.innerHTML = `
            <div class="flex items-center gap-2">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="font-medium">${message}</span>
            </div>
        `;
        
        document.body.appendChild(notification);
        
        // Animate in
        setTimeout(() => {
            notification.classList.remove('translate-x-full');
        }, 100);
        
        // Remove after 3 seconds
        setTimeout(() => {
            notification.classList.add('translate-x-full');
            setTimeout(() => {
                if (document.body.contains(notification)) {
                    document.body.removeChild(notification);
                }
            }, 300);
        }, 3000);
    };

    const handleDeleteImage = (imageId, event) => {
        event.stopPropagation(); // Prevent triggering the click handler
        setImageToDelete(imageId);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (imageToDelete) {
            try {
                await imageUploadService.deleteImage(imageToDelete);
                await loadImages(); // Reload the images
                showNotification('Bild erfolgreich gelöscht!', 'success');
            } catch (error) {
                console.error('Error deleting image:', error);
                showNotification('Fehler beim Löschen des Bildes', 'error');
            }
        }
        setShowDeleteModal(false);
        setImageToDelete(null);
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setImageToDelete(null);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                alert('Bitte wählen Sie eine Bilddatei aus.');
                return;
            }
            
            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                alert('Die Datei ist zu groß. Maximale Größe: 10MB');
                return;
            }
            
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setUploadProgress(0);

        const progressInterval = setInterval(() => {
            setUploadProgress(prev => {
                if (prev >= 90) {
                    clearInterval(progressInterval);
                    return 90;
                }
                return prev + 10;
            });
        }, 100);
        
        try {
            await imageUploadService.uploadImage(selectedFile);
            
            clearInterval(progressInterval);
            setUploadProgress(100);
            
            // Reset form and reload images
            setSelectedFile(null);
            setShowUploadForm(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            
            // Reload images to show the new upload
            await loadImages();
            
        } catch (error) {
            console.error('Upload failed:', error);
            alert(`Upload fehlgeschlagen: ${error.message}`);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <ModernButton onClick={onBack} variant="secondary" size="sm">
                                ← Zurück
                            </ModernButton>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                                    Bildergalerie
                                </h1>
                                <p className="text-slate-600 dark:text-slate-400">
                                    Verwalten Sie Ihre hochgeladenen Bilder
                                </p>
                            </div>
                        </div>
                        <ModernButton
                            onClick={() => setShowUploadForm(!showUploadForm)}
                            variant="primary"
                            size="sm"
                            icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>}
                        >
                            {showUploadForm ? 'Abbrechen' : 'Bild hochladen'}
                        </ModernButton>
                    </div>
                </div>

                {/* Upload Form */}
                {showUploadForm && (
                    <div className="mb-6 p-4 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Bilddatei auswählen
                                </label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    disabled={isUploading}
                                />
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    JPG, PNG, GIF, WebP • Max. 10MB
                                </p>
                            </div>

                            {/* Selected File Name */}
                            {selectedFile && (
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                        Ausgewählte Datei
                                    </label>
                                    <div className="border border-slate-300 dark:border-slate-600 rounded-lg p-3 bg-slate-50 dark:bg-slate-700">
                                        <p className="text-sm text-slate-600 dark:text-slate-400">
                                            {selectedFile.name}
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Upload Progress */}
                            {isUploading && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Upload läuft...
                                        </span>
                                        <span className="text-sm text-slate-500 dark:text-slate-400">
                                            {uploadProgress}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                                        <div
                                            className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3">
                                <ModernButton
                                    onClick={() => setShowUploadForm(false)}
                                    variant="secondary"
                                    size="sm"
                                    disabled={isUploading}
                                >
                                    Abbrechen
                                </ModernButton>
                                <ModernButton
                                    onClick={handleUpload}
                                    variant="primary"
                                    size="sm"
                                    disabled={!selectedFile || isUploading}
                                    icon={isUploading ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                    )}
                                >
                                    {isUploading ? 'Upload läuft...' : 'Hochladen'}
                                </ModernButton>
                            </div>
                        </div>
                    </div>
                )}

                {/* Images Grid */}
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                ) : images.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">
                            Keine Bilder vorhanden
                        </h3>
                        <p className="text-slate-500 dark:text-slate-400">
                            Laden Sie Ihr erstes Bild hoch
                        </p>
                    </div>
                ) : (
                    <div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {images.map((image) => (
                                <div
                                    key={image.id}
                                    className="relative group cursor-pointer rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 aspect-square"
                                >
                                    <img
                                        src={image.url}
                                        alt={image.title || 'Bild'}
                                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                                    />
                                    
                                    {/* Hover Overlay with Actions */}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
                                            {/* Copy Button */}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleImageClick(image.url);
                                                }}
                                                className="bg-green-600 hover:bg-green-700 text-white rounded-full p-2 shadow-lg"
                                                title="URL kopieren"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                            </button>
                                            
                                            {/* Delete Button */}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteImage(image.id, e);
                                                }}
                                                className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg"
                                                title="Bild löschen"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center mb-4">
                            <div className="shrink-0 w-10 h-10 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                                Bild löschen
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                                Möchten Sie dieses Bild wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                            </p>
                            <div className="flex justify-center space-x-3">
                                <ModernButton
                                    onClick={cancelDelete}
                                    variant="secondary"
                                    size="sm"
                                >
                                    Abbrechen
                                </ModernButton>
                                <ModernButton
                                    onClick={confirmDelete}
                                    variant="primary"
                                    size="sm"
                                    className="bg-red-600 hover:bg-red-700"
                                >
                                    Löschen
                                </ModernButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
